/**
 * Conversion Markdown restreinte, destinee aux enonces d'exercice.
 *
 * Motivation : ce module enseigne le HTML. Un enonce qui cite une balise
 * (« pour afficher une image, on utilise <img> ») la voyait interpretee par le
 * navigateur au lieu d'etre montree a l'eleve : la balise disparaissait du
 * texte, et `<img>` produisait une image cassee. Ecrire `&lt;img&gt;` corrige
 * le rendu, mais demande a l'enseignant un echappement manuel a chaque balise
 * citee — peu praticable, et source d'erreurs.
 *
 * Avec Markdown, `` `<img>` `` s'ecrit naturellement et s'affiche
 * litteralement. Les backticks sont traites en premier, precisement pour que
 * leur contenu soit protege avant toute autre transformation.
 *
 * Le sous-ensemble couvert est volontairement reduit a ce que reclame un
 * enonce ; le HTML reste disponible pour les mises en page plus riches.
 */

/** Echappe les caracteres qui seraient autrement interpretes comme du balisage. */
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Marque de substitution d'un fragment mis de cote pendant la conversion.
 *
 * Les caracteres encadrants sont improbables dans un enonce, et l'index rend
 * chaque marque unique.
 */
const placeholder = (index: number) => ` MD${index} `;

/**
 * Indique si un enonce utilise une syntaxe Markdown reconnue.
 *
 * Sert a ne pas transformer les enonces existants : sans marqueur, le texte
 * garde son rendu actuel.
 */
export const looksLikeMarkdown = (text: string): boolean =>
  /`[^`]+`/.test(text) ||
  /\*\*[^*]+\*\*/.test(text) ||
  /!\[[^\]]*\]\([^)]+\)/.test(text) ||
  /^\s*[-*]\s+/m.test(text) ||
  /^\s*#{1,3}\s+/m.test(text);

/**
 * Convertit un enonce Markdown en HTML.
 *
 * Le resultat doit encore etre assaini par l'appelant : cette fonction produit
 * du balisage, elle ne le securise pas.
 */
export const markdownToHtml = (source: string): string => {
  const codeFragments: string[] = [];

  // 1) Mettre de cote le contenu des backticks AVANT toute autre etape.
  //    C'est ce qui permet a `<img>` d'etre affiche tel quel : son contenu est
  //    echappe ici, et ne repasse plus par aucune transformation.
  let text = source.replace(/```([\s\S]*?)```/g, (_match, code: string) => {
    codeFragments.push(`<pre><code>${escapeHtml(String(code).trim())}</code></pre>`);
    return placeholder(codeFragments.length - 1);
  });

  text = text.replace(/`([^`\n]+)`/g, (_match, code: string) => {
    codeFragments.push(`<code>${escapeHtml(String(code))}</code>`);
    return placeholder(codeFragments.length - 1);
  });

  // 2) Mettre de cote les images et les liens : leurs URL ne doivent pas etre
  //    alterees par les transformations de texte qui suivent.
  text = text.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_match, alt: string, src: string) => {
    codeFragments.push(`<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" />`);
    return placeholder(codeFragments.length - 1);
  });

  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, label: string, href: string) => {
    codeFragments.push(
      `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
        label
      )}</a>`
    );
    return placeholder(codeFragments.length - 1);
  });

  // 3) Le reste du texte est echappe : un enonce Markdown ne melange pas les
  //    deux syntaxes, et une balise laissee la serait de toute facon inattendue.
  text = escapeHtml(text);

  // 4) Emphase. `**` est traite avant `*` pour ne pas le couper en deux.
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');

  // 5) Construction par lignes : titres, listes et paragraphes.
  const lines = text.split('\n');
  const blocks: string[] = [];
  let listItems: string[] = [];
  let paragraph: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(`<ul>${listItems.map(item => `<li>${item}</li>`).join('')}</ul>`);
    listItems = [];
  };

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(`<p>${paragraph.join('<br />')}</p>`);
    paragraph = [];
  };

  lines.forEach(line => {
    const trimmed = line.trim();

    if (trimmed === '') {
      flushList();
      flushParagraph();
      return;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushList();
      flushParagraph();
      const level = heading[1].length + 1; // `#` produit un h2 : h1 est le titre de la page.
      blocks.push(`<h${level}>${heading[2]}</h${level}>`);
      return;
    }

    const listItem = trimmed.match(/^[-*]\s+(.*)$/);
    if (listItem) {
      flushParagraph();
      listItems.push(listItem[1]);
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushList();
  flushParagraph();

  let html = blocks.join('');

  // 6) Reinjecter les fragments mis de cote.
  codeFragments.forEach((fragment, index) => {
    html = html.split(placeholder(index)).join(fragment);
  });

  return html;
};
